import { TripShare, TripLocation, TripState } from "types/user";
import { TravelMode, EXPERIENCE_ID, MapLocation, TripLegMode } from "types/map";
import { hasLatLong } from "libraries/map";
import {
  encodePlaceIconCategory,
  getPlaceTypesForIconCode,
} from "libraries/placeTypeIcons";
import {
  encodeTripLegModes,
  decodeTripLegModes,
  hasNonDriveLeg,
  padTripLegModes,
} from "libraries/tripLeg";

export const getNeutralTrip = (): TripState => ({
  carId: "",
  genericVin: "",
  licensePlate: "",
  mapTravelMode: TravelMode.DRIVING,
  distanceMeters: 0,
  distanceLabel: "",
  durationSeconds: 0,
  fromAddress: "",
  fromLatitude: 0,
  fromLongitude: 0,
  toAddress: "",
  toLatitude: 0,
  toLongitude: 0,
  tripId: null,
  tripTimeStamp: "",
  commuteFrequency: 1,
  fromPlaceName: undefined,
  fromPlaceTypes: undefined,
});

export const tripStateToTripLocation = ({
  fromAddress,
  fromLatitude,
  fromLongitude,
  toAddress,
  toLatitude,
  toLongitude,
}: TripState): TripLocation => ({
  fromAddress,
  fromLatitude,
  fromLongitude,
  toAddress,
  toLatitude,
  toLongitude,
});

const locationToCoordinatePair = ({
  latitude,
  longitude,
}: MapLocation): string => `${latitude ?? 0};${longitude ?? 0}`;

export const tripStateToTripShare = (
  {
    fromAddress,
    fromLatitude,
    fromLongitude,
    toAddress,
    toLatitude,
    toLongitude,
    fromPlaceTypes,
  }: TripState,
  experienceId: EXPERIENCE_ID,
  travelMode: TravelMode,
  genericVin: string,
  destinations: MapLocation[] = [],
  legModes: TripLegMode[] = []
): TripShare => {
  const tripShare: TripShare = {
    fromLabel: fromAddress,
    fromCoordinates: `${fromLatitude};${fromLongitude}`,
    toLabel: toAddress,
    toCoordinates: `${toLatitude};${toLongitude}`,
    genericVin,
    experienceId,
    travelMode,
  };
  const placeTypeCodes = [
    encodePlaceIconCategory(fromPlaceTypes),
    ...destinations.map((destination) =>
      encodePlaceIconCategory(destination.placeTypes)
    ),
  ].join("");

  if (placeTypeCodes) {
    tripShare.pt = placeTypeCodes;
  }

  const hopModes = padTripLegModes(
    legModes,
    destinations.filter(hasLatLong).length
  );
  if (hasNonDriveLeg(hopModes)) {
    tripShare.legs = encodeTripLegModes(hopModes);
  }

  const intermediateStops = destinations.slice(0, -1);

  if (intermediateStops.length > 0) {
    tripShare.waypointLabels = intermediateStops
      .map((stop) => stop.formattedAddress ?? "")
      .join("|");
    tripShare.waypointCoordinates = intermediateStops
      .map(locationToCoordinatePair)
      .join("|");
  }

  return tripShare;
};

export const parseWaypointCoordinates = (
  waypointCoordinates: string,
  waypointLabels: string,
  placeTypeCodes = ""
): MapLocation[] => {
  const coordinatePairs = waypointCoordinates.split("|").filter(Boolean);
  const labels = waypointLabels.split("|");

  return coordinatePairs.map((pair, index) => {
    const [lat, lng] = pair.split(";");

    return {
      formattedAddress: labels[index] ?? "",
      latitude: Number(lat || 0),
      longitude: Number(lng || 0),
      placeTypes: getPlaceTypesForIconCode(placeTypeCodes[index]),
    };
  });
};

export const buildDestinationsFromTripShare = (tripShare: TripShare): MapLocation[] => {
  const destinationPlaceTypeCodes = tripShare.pt?.slice(1) ?? "";
  const finalDestination: MapLocation = {
    formattedAddress: tripShare.toLabel,
    latitude: Number(tripShare.toCoordinates.split(";")[0] || 0),
    longitude: Number(tripShare.toCoordinates.split(";")[1] || 0),
    placeTypes: getPlaceTypesForIconCode(destinationPlaceTypeCodes[0]),
  };

  if (!tripShare.waypointCoordinates) {
    return [finalDestination];
  }

  const intermediateStops = parseWaypointCoordinates(
    tripShare.waypointCoordinates,
    tripShare.waypointLabels ?? "",
    destinationPlaceTypeCodes
  );

  finalDestination.placeTypes = getPlaceTypesForIconCode(
    destinationPlaceTypeCodes[intermediateStops.length]
  );

  return [...intermediateStops, finalDestination];
};

export const buildLegModesFromTripShare = (
  tripShare: TripShare,
  hopCount: number
): TripLegMode[] => decodeTripLegModes(tripShare.legs, hopCount);

